import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { Col, Form, Row, Typography } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { USER_PREFERENCES } from '../../../../../../../../contants/common';
import { overChargeClaimGetCarrierInfo } from '../../../../../../../../redux/Claims/OverCharge/OverChargeEffects';
import { setAppLoader } from '../../../../../../../../redux/common/CommonActions';
import useUserPreference from '../../../../../../../../redux/hooks/useUserPreference';
import { formateWithMoment } from '../../../../../../../../utilz/dates';
import { routeFileSelector } from '../../../../../../../../utilz/i18n';
import FormDatePicker from '../../../../../../../components/Inputs/FormDatePicker';
import FormInput from '../../../../../../../components/Inputs/FormInput';
import HiddenFormInput from '../../../../../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../../../../../components/Labels/FormLabel';
import TransportationProviderSelector from '../../../../../../../components/Selectors/AsynchSearch/TransportationProviderSelector';
import ToggleSwitch from '../../../../../../../components/Switch/ToggleSwitch';
import TMSFiledCard from '../../../../../../common/components/TMSFiledCard';

const YourClaimForm = ({ editMode = true, claimDetails, form }) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const dispatch = useDispatch();
  const carrierKey = Form.useWatch('carrierKey', form);
  const carrierName = Form.useWatch('carrierName', form);
  const fbAttach = Form.useWatch('fbAttach', form);
  const bolAttach = Form.useWatch('bolAttach', form);
  const invAttach = Form.useWatch('invAttach', form);
  const otherAttach = Form.useWatch('otherAttach', form);
  const closedDate = Form.useWatch('closedDate', form);
  const ammendmentDate = Form.useWatch('ammendmentDate', form);
  const checkDateToCustomer = Form.useWatch('checkDateToCustomer', form);
  const carrierDisplayAddress = Form.useWatch('carrierDisplayAddress', form);

  const userDateFormat = useUserPreference(USER_PREFERENCES.DEFAULT_DATE_FORMAT);
  const handleCarrierChange = async (data) => {
    try {
      if (data) {
        dispatch(setAppLoader(true));
        const { data: carrierInfo } = await overChargeClaimGetCarrierInfo(data?.carrierKey);
        const payload = {
          carrierKey: carrierInfo?.carrierKey,
          carrierName: data?.carrierName,
          carrierDisplayAddress: carrierInfo?.carrierDisplayAddress,
          carrierEmail: carrierInfo?.carrierEmail,
        };
        form.setFieldsValue(payload);
        dispatch(setAppLoader(false));
      }
    } catch {
      dispatch(setAppLoader(false));
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        message: t('translation:default.notification.failedTrackingDetails'),
      });
    }
  };
  const handleCheckBoxChanges = (name) => (value) => {
    form.setFieldValue(name, Boolean(value));
  };

  return (
    <Row gutter={30}>
      <Col sm={6}>
        <div className="mb-15">
          <FormInput
            name="carrierClaimNo"
            label={t('translation:claims.yourClaim')}
            placeholder={t('translation:claims.enterYourClaim')}
            editMode={editMode}
            maxLength={15}
            // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
            tabIndex={6}
          />
        </div>

        <div className="mb-15">
          {editMode ? (
            <TransportationProviderSelector
              margin="mb-0"
              form={form}
              showPopupFilter
              label={t('translation:claims.mailTo')}
              name="carrierKey"
              inputValue={carrierKey}
              carrierName={carrierName}
              onChange={handleCarrierChange}
              disableOpen
              rules={[
                {
                  required: true,
                  message: 'Please select a Transportation Provider',
                },
              ]}
              // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
              tabIndex={7}
            />
          ) : (
            <TMSFiledCard containerClass="fw-600 " title={t('translation:claims.mailTo')}>
              {carrierName}
            </TMSFiledCard>
          )}
          <HiddenFormInput name="carrierName" />
          <HiddenFormInput name="carrierKey" />
          <HiddenFormInput name="carrierEmail" />
        </div>

        <div className="">
          <TMSFiledCard title={t('translation:default.fields.mailToAddress')}>
            <Typography.Paragraph style={{ whiteSpace: 'pre-line' }}>
              {String(carrierDisplayAddress).replace('\r', '\n')}
            </Typography.Paragraph>
          </TMSFiledCard>
          <HiddenFormInput name="carrierDisplayAddress" />
        </div>
      </Col>
      <Col sm={6}>
        <div>
          <FormLabel className="lh-0 ">{t('translation:claims.attachedDocumentation')}</FormLabel>
          <HiddenFormInput name="fbAttach" />
          <HiddenFormInput name="bolAttach" />
          <HiddenFormInput name="invAttach" />
          <HiddenFormInput name="otherAttach" />
          <Row className="mt-5 mb-15">
            <Col sm={12}>
              <TMSFiledCard title={t('translation:claims.freightBillProNumber')}>
                <ToggleSwitch
                  checkedChildren="On"
                  unCheckedChildren="Off"
                  checked={fbAttach}
                  onClick={handleCheckBoxChanges('fbAttach')}
                  disabled={!editMode}
                  // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
                  tabIndex={8}
                />
              </TMSFiledCard>
            </Col>
            <Col sm={12}>
              <TMSFiledCard title={t('translation:claims.houseBillOfLadingNumber')}>
                <ToggleSwitch
                  checkedChildren="On"
                  unCheckedChildren="Off"
                  checked={bolAttach}
                  onClick={handleCheckBoxChanges('bolAttach')}
                  disabled={!editMode}
                  // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
                  tabIndex={9}
                />
              </TMSFiledCard>
            </Col>
          </Row>
          <Row className="mb-18">
            <Col sm={12}>
              <TMSFiledCard title={t('translation:default.fields.invoiceNumber')}>
                <ToggleSwitch
                  checkedChildren="On"
                  unCheckedChildren="Off"
                  checked={invAttach}
                  onClick={handleCheckBoxChanges('invAttach')}
                  disabled={!editMode}
                  // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
                  tabIndex={10}
                />
              </TMSFiledCard>
            </Col>
            <Col sm={12}>
              <TMSFiledCard title={t('translation:claims.others')}>
                <ToggleSwitch
                  checkedChildren="On"
                  unCheckedChildren="Off"
                  checked={otherAttach}
                  onClick={handleCheckBoxChanges('otherAttach')}
                  disabled={!editMode}
                  // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
                  tabIndex={11}
                />
              </TMSFiledCard>
            </Col>
          </Row>
        </div>
        <FormInput
          margin="mb-0"
          label={t('translation:claims.othersPleaseMention')}
          editMode={editMode && otherAttach}
          name="otherDesc"
          tabIndex={editMode && otherAttach ? 12 : -1}
        />
      </Col>
      <Col sm={6}>
        <div className="mb-28 mt-3">
          <TMSFiledCard title={t('translation:claims.recoverySum')}>
            <span className="text-success fw-800">
              <NumericFormat
                thousandsGroupStyle="lakh"
                displayType="text"
                value={String(claimDetails?.recoverySum ? claimDetails?.recoverySum : 0)}
                thousandSeparator
                fixedDecimalScale
                decimalScale={2}
              />
            </span>
          </TMSFiledCard>
        </div>
        <div className="mb-15">
          {editMode ? (
            <FormDatePicker
              name="ammendmentDate"
              editMode={Boolean(editMode)}
              margin="m-0"
              label={t('translation:default.fields.amendmentDate')}
              allowClear={false}
              // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
              tabIndex={13}
            />
          ) : (
            <TMSFiledCard title={t('translation:default.fields.amendmentDate')}>
              {ammendmentDate ? formateWithMoment(ammendmentDate, userDateFormat) : ''}
            </TMSFiledCard>
          )}
          <HiddenFormInput name="ammendmentDate" />
        </div>
        <div>
          {editMode ? (
            <FormDatePicker
              name="closedDate"
              editMode={Boolean(editMode)}
              margin="m-0"
              label={t('translation:claims.dateClosed')}
              allowClear={false}
              // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
              tabIndex={14}
            />
          ) : (
            <TMSFiledCard title={t('translation:claims.dateClosed')}>
              {closedDate ? formateWithMoment(closedDate, userDateFormat) : ''}
            </TMSFiledCard>
          )}
          <HiddenFormInput name="closedDate" />
        </div>
      </Col>
      <Col sm={6}>
        <FormInput
          name="checkNoFromCarrier"
          label={t('translation:claims.checkNumberFromTransportationProvider')}
          editMode={editMode}
          maxLength={20}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={15}
        />
        <FormInput
          name="checkNoToCustomer"
          label={t('translation:claims.checkNumberToCustomer')}
          editMode={editMode}
          maxLength={20}
          // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
          tabIndex={16}
        />
        <div>
          {editMode ? (
            <FormDatePicker
              name="checkDateToCustomer"
              editMode={Boolean(editMode)}
              margin="m-0"
              label={t('translation:claims.dateRefundedToCustomer')}
              allowClear={false}
              // biome-ignore lint/a11y/noPositiveTabindex: <explanation>
              tabIndex={17}
            />
          ) : (
            <TMSFiledCard title={t('translation:claims.dateRefundedToCustomer')}>
              {checkDateToCustomer ? formateWithMoment(checkDateToCustomer, userDateFormat) : ''}
            </TMSFiledCard>
          )}
          <HiddenFormInput name="checkDateToCustomer" />
        </div>
      </Col>
    </Row>
  );
};

export default YourClaimForm;
